// 購物車 / 付款別 → OrderDetailPrintData 轉換器
// 型別見 orderDetailEscPosBuilder.js 的 JSDoc。
// 規格文字複用 cart getter，與結帳頁 productConfirm.vue 顯示一致。
import moment from 'moment'
import { getPrintName, getSpecOptionPrintName } from '@/utils/productName.utils'
import { getComboSubSelections } from '@/utils/comboSub.utils'

// consumptionPattern → order_type 文字（對照 OrderConfirm.vue / Payment.vue）
const ORDER_TYPE_TEXT = {
  0: '門市內用',
  1: '現場外帶',
  2: '預約自取',
  3: '店家外送',
  4: '零售自取',
  5: '零售宅配',
}

// 規格選項 → 顯示字串陣列（列印端統一格式，直接讀原始 count/price，不走 cart getter）：
// 加價接「$x」、負值「-$x」；數量 >1 補「 x{數量}」。
// （cart getter customizedText 僅在加價>0 時附 x{數量}，無加價/減價會省略數量，
//   故列印自行由原始資料組字串，不更動 getter，結帳頁/訂單清單顯示維持原樣）
function specOptionTexts(counts, options) {
  const opts = []
  ;(counts || []).forEach((count, i) => {
    if (count !== 0 && options && options[i]) {
      const name = getSpecOptionPrintName(options[i])
      const price = Number(options[i].price)
      const qty = count > 1 ? ` x${count}` : ''
      if (price > 0) opts.push(`${name}$${price}${qty}`)
      else if (price < 0) opts.push(`${name}-$${Math.abs(price)}${qty}`)
      else opts.push(`${name}${qty}`)
    }
  })
  return opts
}

// 一般商品規格：所有規格群組的選項值（無群組標題）。備註不併入，改由主商品名稱後括弧顯示。
function productSpecText(product) {
  const parts = []
  for (const setting of product.settings || []) {
    const opts = specOptionTexts(setting.subSpecOptionSelectCount, setting.specOptions)
    if (opts.length) parts.push(opts.join(', '))
  }
  return parts.join(', ')
}

// 全單規格：各規格群組被選的選項值（含加價/數量），每群組一行
function orderSpecText(orderSpecs) {
  const lines = []
  for (const os of orderSpecs || []) {
    const opts = specOptionTexts(os.subSpecOptionSelectCount, os.productSpec?.specOptions)
    if (opts.length) lines.push(opts.join(', '))
  }
  return lines.join('\n')
}

// 套餐子商品規格：讀原始 comboSub.productSpecSettings（與一般商品同格式，保留數量）
function comboSubSpecText(productSpecSettings) {
  const opts = []
  for (const spec of productSpecSettings || []) {
    opts.push(...specOptionTexts(spec.subSpecOptionSelectCount, spec.specOptions))
  }
  return opts.join(', ')
}

// 由原始 comboSub 組出一個子商品（名稱含加價、規格含數量）
// item_count = 該子項選取數量 × 套餐份數（例：2 份套餐、每份 1 個吐司 → 2x吐司）
function buildComboSub(comboSub, subCount, comboCount) {
  let name = getPrintName(comboSub.name)
  // 子商品加價：名稱後接 +$x（無空格），負值為 -$x
  if (comboSub.altAddonPrice) {
    const n = Number(comboSub.altAddonPrice)
    name += n > 0 ? `+$${n}` : `-$${Math.abs(n)}`
  }
  return {
    item_name: name,
    item_spec: comboSubSpecText(comboSub.productSpecSettings),
    item_count: (subCount > 0 ? subCount : 1) * comboCount,
  }
}

// 套餐子商品：比照 cart getter customizedText 的走訪順序，但直接讀原始資料以保留規格數量
// （getter 僅在加價>0 時附 x{數量}，無加價/減價的子項規格多選數量會遺失，故列印自行走訪原始結構）
function comboSubItems(product) {
  const comboCount = product.quantity || 1
  const subs = []
  // 固定餐點
  for (const sub of product.fixedProducts || []) {
    subs.push(buildComboSub(sub, 1, comboCount))
  }
  // 套餐子項任選（含加價商品）；可重複選時每份一筆、各自帶規格
  for (const group of product.comboSubItem || []) {
    for (const { product: sub, count } of getComboSubSelections(group)) {
      subs.push(buildComboSub(sub, count, comboCount))
    }
  }
  // 任選子項
  for (const sub of product.comboSubAnyProducts?.orderProducts || []) {
    subs.push(buildComboSub(sub, 1, comboCount))
  }
  return subs
}

function buildItems(rootState, rootGetters) {
  return rootState.cart.cart.map((product) => {
    const base = {
      item_name: getPrintName(product.product.name),
      // 單價：builder 會自動 ×數量印總價
      item_price: Number(rootGetters['cart/productUnitPrice'](product)),
      item_count: product.quantity,
    }
    if (product.isCombo) {
      // 套餐：輸出 is_combo + 子商品陣列，交由 builder 以子項排版列印
      return { ...base, is_combo: 1, item_note: product.note || '', items: comboSubItems(product) }
    }
    // 備註以 item_note 傳出，主商品名稱後以括弧顯示
    return { ...base, item_note: product.note || '', item_spec: productSpecText(product) }
  })
}

function zoneTextOf(rootState, pattern) {
  if (pattern === 1) {
    const t = rootState.cart.takeOutTableData || {}
    return t.tableName || ''
  }
  const t = rootState.cart.tableData || {}
  return t.tableName || t.tableIndex || ''
}

function deliverTimeFieldsOf(payment) {
  // 依 consumptionPattern 將 deliverDateTime 放入對應欄位
  const pattern = payment.consumptionPattern || 0
  // 門市內用/現場外帶為現場即時訂單，無預約時間 → 以下單當下時間為用餐/取餐時間
  if (pattern === 0) return { meal_time: moment().format('YYYY-MM-DD HH:mm') }
  if (pattern === 1) return { get_meal_time: moment().format('YYYY-MM-DD HH:mm') }

  const date = payment.pickupDate
  if (!date) return {}
  // 零售宅配的時間為「時段」(deliveryPeriod)，其餘使用 pickupTime
  const time = pattern === 5 ? payment.deliveryPeriod : payment.pickupTime
  const text = `${date} ${time || ''}`.trim()
  switch (pattern) {
    case 2: // 預約自取 → 取餐時間
      return { get_meal_time: text }
    case 4: // 零售自取 → 取貨時間
      return { get_order_time: text }
    case 3: // 店家外送 → 送達時間
    case 5: // 零售宅配 → 送達時間(時段)
      return { delivery_time: text }
    default:
      return {}
  }
}

/**
 * @param {Object} ctx
 * @param {Object} ctx.rootState
 * @param {Object} ctx.rootGetters
 * @param {'cash'|'linePay'} ctx.paymentType
 * @param {string} [ctx.orderNumber]  現金時可能為空字串（建立前未知）
 * @param {number} [ctx.printWidth]  紙張寬度 58 或 80，預設 58
 * @returns {Object} OrderDetailPrintData
 */
export function buildOrderDetailPrintData({ rootState, rootGetters, paymentType, orderNumber, printWidth }) {
  const payment = rootState.cart.payment || {}
  const pattern = payment.consumptionPattern || 0
  const isLinePay = paymentType === 'linePay'
  const items = buildItems(rootState, rootGetters)

  const data = {
    width: printWidth === 80 ? 80 : 58,
    print_count: 1,
    title: '商品明細',
    order_number: orderNumber || '',
    order_type: ORDER_TYPE_TEXT[pattern] || '',
    order_time: moment().format('YYYY-MM-DD HH:mm'),
    // 現金成立時尚未收款 → 未結帳；LINE Pay 確認付款後才印 → 已付款
    order_status: isLinePay ? '已付款' : '未結帳',
    payment_method: isLinePay ? 'LINE Pay(已付款)' : '現金(未結帳)',
    payment_status: isLinePay ? '已付款' : '未結帳',
    zone: zoneTextOf(rootState, pattern),
    customer_name: payment.userName || '',
    customer_tel: payment.userMobile || '',
    recipient: payment.recipientName || '',
    contact_phone: payment.phone || '',
    delivery_address: payment.address || '',
    order_comment: payment.comment || '',
    // 發票資訊：依 invoiceTypeIndex 只帶選中的那一種（對照 OrderConfirm.vue createOnlineOrder）
    // 1=手機載具、2=統一編號、3=愛心碼；0/未選=不印
    invoice_carrier: payment.invoiceTypeIndex === 1 ? (payment.invoiceCarrier || '') : '',
    invoice_vat_number: payment.invoiceTypeIndex === 2 ? (payment.invoiceVatNumber || '') : '',
    invoice_love_code: payment.invoiceTypeIndex === 3 ? (payment.invoiceNpoBan || '') : '',
    order_spec: orderSpecText(rootState.order.orderSpecs),
    items,
    // 小計項數：套餐以子商品數量總和計，一般商品以數量計
    item_counts: items.reduce(
      (n, it) =>
        it.is_combo
          ? n + (it.items || []).reduce((s, sub) => s + (sub.item_count || 0), 0)
          : n + (it.item_count || 0),
      0
    ),
    subtotal: rootGetters['cart/cartTotalPrice'],
    // 紅利積分折抵：僅折抵啟用且 >0 時帶負值（現金/LINE Pay 皆適用）
    point_discount:
      rootState.cart.isPointUsed && rootState.cart.points > 0
        ? `-${rootState.cart.points}`
        : '',
    // 優惠券折抵：有套用時帶負值，格式比照紅利積分折抵
    coupon_discount:
      rootGetters['cart/selectedCouponValue'] > 0
        ? `-${rootGetters['cart/selectedCouponValue']}`
        : '',
    service_fee: Number(rootGetters['cart/displayServiceFee']) || '',
    service_fee_name: rootGetters['basic/serviceFeeName'],
    shipping_fee: Number(rootGetters['cart/displayShippingFee']) || '',
    total_amount: rootGetters['cart/displayCartTotalPrice'],
    ...deliverTimeFieldsOf(payment),
  }
  return data
}
